import * as React from "react";
import {
    DateFormats,
    IAvailabilitySchedule,
    IClinician,
    RecurrenceRule,
    IIntakeAvailability,
} from "intake_shared/src/types";
import { CacheContext } from "../../../../contexts/CacheContext";
import {
    getDayTimeFromScheduleDate,
    getNumberOfWeeks,
} from "../../../../utils/utils";
import moment from "moment";
import {
    CalendarId,
    getV2ColorStylesForCalendarId,
    RECURRENCE_COLORS,
} from "../../../../constants";
import { RRule } from "rrule";
import { AlertDialog } from "../../bits/AlertDialog/AlertDialog";
import { Button } from "../../bits/Button/Button";
import { Badge } from "../../bits/Badge/Badge";
import BigCalendar from "../BigCalendar/BigCalendar";
import styles from "./CalendarBoxView.module.scss";
import { getIntakeStatusBadgeProps } from "../../../v2utils";
import EditScheduleDialog from "../EditScheduleDialog/EditScheduleDialog";
import DeleteScheduleDialog from "../DeleteScheduleDialog/DeleteScheduleDialog";
import { AclContext } from "../../../../contexts/AclContext";
import EditIcon from "../../icons/Edit";

interface CalendarBoxViewProps {
    schedules: IAvailabilitySchedule[];
    onRemoveSchedule: (schedule: IAvailabilitySchedule) => void;
    onRemoveOfficeWindow?: (schedule: IAvailabilitySchedule) => void;
    onUpdateSchedule: (schedule: IAvailabilitySchedule) => void;
    onSelectSchedule?: (
        schedule: IAvailabilitySchedule
    ) => Promise<IAvailabilitySchedule[]>;
    loggedInClinician: IClinician | undefined;
    currentView?: any;
    currentDate?: Date;
    onViewChange?: (view: any) => void;
    onDateChange?: (date: Date) => void;
    officeSchedulesInReview?: IAvailabilitySchedule[];
    setRefreshKey?: (value: React.SetStateAction<number>) => void;
    // Intake session handlers
    onEditIntakeSession?: (schedule: IAvailabilitySchedule) => void;
    intakeSessionAvailability?: IIntakeAvailability[];
}

export default function CalendarBoxView({
    schedules,
    onRemoveSchedule,
    onRemoveOfficeWindow,
    onUpdateSchedule,
    onSelectSchedule,
    loggedInClinician,
    currentView,
    currentDate,
    onViewChange,
    onDateChange,
    officeSchedulesInReview,
    setRefreshKey,
    onEditIntakeSession,
    intakeSessionAvailability = [],
}: CalendarBoxViewProps) {
    const [scheduleToEdit, setScheduleToEdit] =
        React.useState<IAvailabilitySchedule | null>(null);
    const [showDialog, setShowDialog] = React.useState(false);
    const [eventDetails, setEventDetails] = React.useState({} as any);
    const [events, setEvents] = React.useState([] as IAvailabilitySchedule[]);
    const olc = React.useContext(CacheContext);
    const userAcl = React.useContext(AclContext)?.authUser?.acl;
    const [showDeleteScheduleDialog, setShowDeleteScheduleDialog] =
        React.useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
    React.useEffect(() => {
        const generateEvents = async () => {
            const allEvents = [] as IAvailabilitySchedule[];
            for (const schedule of schedules.filter((s) => s.isVisible)) {
                if (!schedule.start || !schedule.end) {
                    console.warn("Schedule missing start/end times:", schedule);
                    continue;
                }

                if (!schedule.title) {
                    console.warn("Schedule missing title:", schedule);
                }

                const numberOfWeeks = getNumberOfWeeks(schedule.recurrenceRule);
                const startingMoment = moment(schedule.start);
                let endDate = schedule.ending_date
                    ? moment(schedule.ending_date)
                    : null;

                let occurrences = [] as any;
                let recurrenceRule;

                if (schedule.calendarId === CalendarId.OFFICE) {
                    recurrenceRule = {
                        freq: RRule.WEEKLY,
                        interval: numberOfWeeks,
                        dtstart: startingMoment.toDate(),
                    };
                }
                if (endDate && schedule.recurrenceRule !== RecurrenceRule.ONE_OFF) {
                    recurrenceRule = {
                        freq: RRule.WEEKLY,
                        interval: numberOfWeeks,
                        dtstart: startingMoment.toDate(),
                    };
                }
                if (!endDate && schedule.recurrenceRule !== RecurrenceRule.ONE_OFF) {
                    // If there is no ending date and it's not a one-off event,
                    // set a default count to end at the last day of the current year to prevent an infinite loop
                    recurrenceRule = {
                        freq: RRule.WEEKLY,
                        interval: numberOfWeeks,
                        dtstart: startingMoment.toDate(),
                    };
                    endDate = moment().endOf("year");
                }
                if (schedule.recurrenceRule === RecurrenceRule.ONE_OFF) {
                    occurrences.push(startingMoment.toDate());
                } else {
                    const rruleOptions = {
                        ...recurrenceRule,
                        until: (endDate as moment.Moment).toDate(),
                    };

                    occurrences = new RRule(rruleOptions).all();
                }
                occurrences.forEach((date) => {
                    const event = {
                        ...schedule,
                        start: moment(date)
                            .set({
                                hour: moment(schedule.start).hours(),
                                minute: moment(schedule.start).minutes(),
                            })
                            .toDate(),
                        end: moment(date)
                            .set({
                                hour: moment(schedule.end).hours(),
                                minute: moment(schedule.end).minutes(),
                            })
                            .toDate(),
                        isIntakeSession: (schedule as any).isIntakeSession,
                        isIntakeSessionAvailable: (schedule as any)
                            .isIntakeSessionAvailable,
                        isIntakeSessionBooked: (schedule as any)
                            .isIntakeSessionBooked,
                        calendarId: schedule.calendarId,
                        bookingId: (schedule as any).bookingId,
                        client_id: (schedule as any).client_id,
                    };

                    allEvents.push(event);
                });
            }
            setEvents(allEvents);
        };
        generateEvents();
    }, [schedules]);

    const CustomMonthEvent = ({ event }: { event: IAvailabilitySchedule }) => (
        <span>
            <span
                className={styles.customWeekEvent}
                style={{
                    color: `${
                        getV2ColorStylesForCalendarId(event.calendarId).titleColor
                    }`,
                }}
            >
                {`${event.title}`},{" "}
            </span>
            {`${getDayTimeFromScheduleDate(
                event.start,
                "h:mma"
            )} - ${getDayTimeFromScheduleDate(event.end, "h:mma")}`}{" "}
        </span>
    );

    const CustomDayEvent = ({ event }: { event: IAvailabilitySchedule }) => (
        <span
            className={styles.customWeekEvent}
            style={{
                color: `${
                    getV2ColorStylesForCalendarId(event.calendarId).titleColor
                }`,
            }}
        >
            {`${event.title}`}
        </span>
    );

    const CustomWeekEvent = ({ event }: { event: IAvailabilitySchedule }) => (
        <span
            className={styles.customWeekEvent}
            style={{
                color: `${
                    getV2ColorStylesForCalendarId(event.calendarId).titleColor
                }`,
            }}
        >
            {`${event.title}`}
        </span>
    );

    const components = {
        month: {
            event: CustomMonthEvent,
        },
        day: {
            event: CustomDayEvent,
        },
        week: {
            event: CustomWeekEvent,
        },
    };

    const eventStyleGetter = (event) => {
        const calendarId = event.calendarId;
        const colorStyles = getV2ColorStylesForCalendarId(calendarId);
        let style = {
            backgroundColor: colorStyles.backgroundColor,
            color: "var(--color-gray)",
            fontWeight: "var(--font-medium)",
            borderRadius: "var(--radius-8)",
            border: `1px solid ${colorStyles.borderColor}`,
            outline: "none",
        };
        return {
            style: style,
        };
    };

    const handleDelete = async () => {
        // Check if it's an intake session - show confirmation dialog
        if (
            (eventDetails as any).isIntakeSession ||
            eventDetails.calendarId === CalendarId.INTAKE_SESSION_AVAILABILITY
        ) {
            setShowDialog(false);
            // Show delete confirmation dialog for intake sessions
            setShowDeleteDialog(true);
            return;
        }

        if (
            eventDetails.action === "clinician_deleting_office_window" &&
            !userAcl?.super_admin &&
            eventDetails.location !== "Remote"
        ) {
            setShowDialog(false);
            setShowDeleteScheduleDialog(true);
        } else {
            setShowDialog(false);
            setShowDeleteDialog(true);
        }
    };
    const getDeleteDialogDescription = () => {
        if (
            ((eventDetails as any).isIntakeSession &&
                (eventDetails as any).isIntakeSessionBooked) ||
            (eventDetails.calendarId === CalendarId.INTAKE_SESSION_AVAILABILITY &&
                (eventDetails as any).isIntakeSessionBooked)
        ) {
            return (
                <>
                    Are you sure you want to cancel this session? If you only need to
                    change the schedule, click the <EditIcon /> icon outside of this
                    modal to reschedule instead.
                    <strong>
                        <p>
                            {eventDetails.title}:{" "}
                            {`${getDayTimeFromScheduleDate(
                                eventDetails?.start,
                                "h:mma"
                            )} - ${getDayTimeFromScheduleDate(
                                eventDetails?.end,
                                "h:mma"
                            )}`}
                        </p>
                    </strong>
                </>
            );
        }

        const hasConflicts =
            (eventDetails.action === "admin_deleting_clinician_office_window" &&
                userAcl?.super_admin) ||
            (userAcl?.super_admin &&
                eventDetails.action === "clinician_deleting_office_window") ||
            (eventDetails.action === "clinician_deleting_office_window" &&
                eventDetails.location === "Remote");

        if (hasConflicts && onSelectSchedule) {
            return (
                <div>
                    We found availability slots that uses this office window and we
                    are going to delete them.
                    <br />
                    Are you sure you want to delete this schedule?
                    <strong>
                        <p>
                            {eventDetails.title}:{" "}
                            {`${getDayTimeFromScheduleDate(
                                eventDetails?.start,
                                "h:mma"
                            )} - ${getDayTimeFromScheduleDate(
                                eventDetails?.end,
                                "h:mma"
                            )}`}
                        </p>
                    </strong>
                </div>
            );
        }

        return (
            <>
                Are you sure you want to delete this schedule?
                <strong>
                    <p>
                        {eventDetails.title}:{" "}
                        {`${getDayTimeFromScheduleDate(
                            eventDetails?.start,
                            "h:mma"
                        )} - ${getDayTimeFromScheduleDate(
                            eventDetails?.end,
                            "h:mma"
                        )}`}
                    </p>
                </strong>
            </>
        );
    };

    const handleDeleteConfirm = async () => {
        if (
            (eventDetails as any).isIntakeSession ||
            eventDetails.calendarId === CalendarId.INTAKE_SESSION_AVAILABILITY
        ) {
            onRemoveSchedule(eventDetails);
            setShowDeleteDialog(false);
            return;
        }

        if (
            eventDetails.action === "clinician_deleting_availability" ||
            eventDetails.action === "admin_deleting_clinician_office_window"
        ) {
            onRemoveSchedule(eventDetails);
        } else if (
            (userAcl?.super_admin &&
                eventDetails.action === "clinician_deleting_office_window") ||
            (eventDetails.action === "clinician_deleting_office_window" &&
                eventDetails.location === "Remote")
        ) {
            onRemoveOfficeWindow && onRemoveOfficeWindow(eventDetails);
        }
        setShowDeleteDialog(false);
    };

    return (
        <>
            <AlertDialog
                open={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
                title={
                    (eventDetails as any).isIntakeSessionBooked
                        ? "Cancel Session"
                        : "Delete Schedule"
                }
                description={getDeleteDialogDescription()}
                saveText="Delete"
                cancelText="Cancel"
                onSaveClick={handleDeleteConfirm}
                onCancelClick={() => setShowDeleteDialog(false)}
                isDeleting={true}
            />
            <AlertDialog
                open={showDialog}
                onClose={() => setShowDialog(false)}
                hideFooter={true}
                title=""
                description={
                    <div>
                        <div className={styles.eventDetailsTitle}>
                            {eventDetails?.title} -{"  "}
                            <span>
                                {moment(eventDetails?.start)?.format(
                                    DateFormats.DATE_FORMAT
                                )}{" "}
                                {""}
                                {`${getDayTimeFromScheduleDate(
                                    eventDetails?.start,
                                    "h:mma"
                                )} - ${getDayTimeFromScheduleDate(
                                    eventDetails?.end,
                                    "h:mma"
                                )}`}
                            </span>
                        </div>
                        <div>
                            <span>
                                <Badge
                                    content={eventDetails.recurrenceRule}
                                    bgColor={
                                        RECURRENCE_COLORS[
                                            (eventDetails.recurrenceRule
                                                ?.trim()
                                                .toLowerCase() ||
                                                RecurrenceRule.WEEKLY) as string
                                        ]
                                    }
                                    textColor="var(--color-white)"
                                />
                            </span>
                            {(eventDetails as any).isIntakeSession && (
                                <span style={{ marginLeft: 8 }}>
                                    {(() => {
                                        const { bgColor, label } =
                                            getIntakeStatusBadgeProps(
                                                (eventDetails as any).status
                                            );
                                        return (
                                            <Badge
                                                content={label}
                                                bgColor={bgColor}
                                                textColor="var(--color-white)"
                                                className={styles.intakeStatusBadge}
                                            />
                                        );
                                    })()}
                                </span>
                            )}
                            {eventDetails.availableService ? (
                                <p>
                                    {
                                        (olc?.requestedServices || {})[
                                            eventDetails.availableService
                                        ]
                                    }
                                </p>
                            ) : (
                                <></>
                            )}
                        </div>
                        <div className={styles.actionButtons}>
                            {!eventDetails.isReadOnly && (
                                <>
                                    {!officeSchedulesInReview?.some(
                                        (reviewSchedule) =>
                                            reviewSchedule.id === eventDetails.id
                                    ) ? (
                                        <Button
                                            variant="danger"
                                            onClick={handleDelete}
                                        >
                                            Delete
                                        </Button>
                                    ) : (
                                        <Badge tone="warning" content="In review" />
                                    )}
                                </>
                            )}
                            {eventDetails.canEdit && (
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        setShowDialog(false);
                                        // Check if it's an intake session - call parent handler
                                        if (
                                            (eventDetails as any).isIntakeSession ||
                                            eventDetails.calendarId ===
                                                CalendarId.INTAKE_SESSION_AVAILABILITY
                                        ) {
                                            if (onEditIntakeSession) {
                                                onEditIntakeSession(eventDetails);
                                            }
                                        } else {
                                            // For regular schedules, use EditScheduleDialog
                                            setScheduleToEdit(eventDetails);
                                        }
                                    }}
                                >
                                    Edit
                                </Button>
                            )}
                        </div>
                    </div>
                }
            />
            <BigCalendar
                events={events}
                components={components}
                setShowPopup={setShowDialog}
                setEventDetails={setEventDetails}
                eventStyleGetter={eventStyleGetter}
                currentView={currentView}
                currentDate={currentDate}
                onViewChange={onViewChange}
                onDateChange={onDateChange}
            />
            <EditScheduleDialog
                schedules={schedules}
                schedule={scheduleToEdit}
                onUpdateSchedule={onUpdateSchedule}
                setScheduleToEdit={setScheduleToEdit}
                loggedInClinician={loggedInClinician}
            />
            <DeleteScheduleDialog
                schedule={eventDetails}
                setScheduleToEdit={setScheduleToEdit}
                loggedInClinician={loggedInClinician}
                setShowDeleteScheduleDialog={setShowDeleteScheduleDialog}
                showDeleteScheduleDialog={showDeleteScheduleDialog}
                setRefreshKey={setRefreshKey}
            />
        </>
    );
}
